import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FormLabel, Switch as MuiSwitch, } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from '@zurich/common/hooks';
import { alpha, styled } from '../../utils';
import { Typography } from '../Typography';
export function TriStateSwitch(props) {
    const { labelFalse, labelTrue, onChange, value, ...otherProps } = props;
    const { t } = useTranslation();
    const handleChange = useCallback((event, newValue) => {
        if (onChange === undefined) {
            return;
        }
        if (value === null) {
            const { left, right } = event.currentTarget.getBoundingClientRect();
            const centerX = (left + right) / 2;
            const firstValue = 'clientX' in event.nativeEvent &&
                event.nativeEvent.clientX >= centerX;
            onChange(firstValue);
            return;
        }
        onChange(newValue);
    }, [onChange, value]);
    return (_jsxs(FormLabelStyled, { children: [_jsx(TypographyStyled, { component: "span", disabled: otherProps.disabled, active: value === false && !otherProps.disabled, children: labelFalse || t('common:no') }), _jsx(SwitchStyled, { init: value === null, checked: value === null ? false : value, onChange: handleChange, ...otherProps }), _jsx(TypographyStyled, { component: "span", disabled: otherProps.disabled, active: value === true && !otherProps.disabled, children: labelTrue || t('common:yes') })] }));
}
const FormLabelStyled = styled(FormLabel)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(min-content, max-content))',
    gap: theme.spacing(2),
    placeItems: 'center',
}));
const TypographyStyled = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'disabled' && prop !== 'active',
})(({ theme, disabled, active }) => ({
    ...((disabled || active) && {
        color: disabled ? theme.palette.white.dark : theme.palette.primary.main,
    }),
}));
const SwitchStyled = styled(MuiSwitch, {
    shouldForwardProp: (prop) => prop !== 'init',
})(({ theme, init }) => ({
    width: 72,
    height: '20px',
    display: 'flex',
    padding: 0,
    overflow: 'initial',
    [theme.breakpoints.up('lg')]: {
        width: 80,
        height: '24px',
    },
    ...(init && {
        '&  .MuiSwitch-switchBase': {
            '& .MuiSwitch-thumb': {},
        },
    }),
    '& .MuiSwitch-thumb': {
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.primary.main}`,
        boxShadow: 'none',
        width: '24px',
        height: '24px',
        [theme.breakpoints.up('lg')]: {
            width: '32px',
            height: '32px',
        },
        ...(init && {
            backgroundColor: theme.palette.primary.main,
        }),
    },
    '& .MuiSwitch-switchBase': {
        padding: 0,
        top: '50%',
        left: 0,
        transform: 'translate(0, -50%)',
        color: theme.palette.white.dark,
        fontFamily: 'zurich-icons',
        speak: 'never',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontVariant: 'normal',
        textTransform: 'none',
        lineHeight: '1',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        ...(init && {
            left: '50%',
            transform: 'translate(-50%, -50%)',
            '&:before': {
                content: 'none',
            },
        }),
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
        },
        '&.Mui-checked': {
            transform: 'translate(-100%, -50%)',
            left: '100%',
            color: theme.palette.common.white,
            '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
            '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: theme.palette.common.white,
            },
            '& .MuiSwitch-thumb': {
                opacity: 1,
                backgroundColor: theme.palette.primary.main,
            },
        },
        '&.Mui-disabled': {
            '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: theme.palette.common.white,
                borderColor: theme.palette.white.dark,
            },
            '& .MuiSwitch-thumb': {
                backgroundColor: `${theme.palette.common.white} !important`,
                borderColor: theme.palette.white.dark,
                [theme.breakpoints.up('lg')]: {
                    backgroundColor: `${theme.palette.white.dark} !important`,
                },
            },
        },
    },
    '& .MuiSwitch-track': {
        opacity: 1,
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: 10,
        backgroundColor: theme.palette.common.white,
        [theme.breakpoints.up('lg')]: {
            borderRadius: 16,
        },
    },
}));
